import { jsx as _jsx } from "react/jsx-runtime";
import { cn } from '@/lib/utils';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';
const Tabs = TabsPrimitive.Root;
/** Breathing room kept beside the active tab when it is scrolled into view. */
const REVEAL_MARGIN_PX = 16;
/**
 * Scroll an overflowing tab row so its active tab is fully visible. Adjusts the
 * list's own scrollLeft (never element.scrollIntoView, which would also scroll
 * every ancestor, e.g. a whole settings modal body, vertically).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function revealActiveTab(list) {
    if (list.scrollWidth <= list.clientWidth)
        return;
    const active = list.querySelector('[role="tab"][data-state="active"]');
    if (!active)
        return;
    const listBox = list.getBoundingClientRect();
    const tabBox = active.getBoundingClientRect();
    if (tabBox.left < listBox.left) {
        list.scrollLeft -= listBox.left - tabBox.left + REVEAL_MARGIN_PX;
    }
    else if (tabBox.right > listBox.right) {
        list.scrollLeft += tabBox.right - listBox.right + REVEAL_MARGIN_PX;
    }
}
function TabsList({ ref, className, ...props }) {
    const listRef = React.useRef(null);
    const setRefs = React.useCallback((node) => {
        listRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    // Keep the active tab in view: on mount (a deep link can select a tab past
    // the fold) and whenever the selection changes, however it changed.
    React.useLayoutEffect(() => {
        const list = listRef.current;
        if (!list)
            return;
        revealActiveTab(list);
        if (typeof MutationObserver === 'undefined')
            return;
        const observer = new MutationObserver(() => revealActiveTab(list));
        observer.observe(list, { subtree: true, attributeFilter: ['data-state'] });
        return () => observer.disconnect();
    }, []);
    return (_jsx(TabsPrimitive.List, { ref: setRefs, className: cn(
        // A row wider than its container scrolls sideways instead of pushing the
        // page wider (phones, iPad portrait). justify-center-safe keeps a row
        // that fits centred as before, but start-aligns one that overflows so
        // the first tab is not cut off out of reach. The p-1 leaves room for
        // the triggers' focus ring inside the scroller. On touch-first devices
        // the list grows with its 40px triggers.
        'bg-muted text-muted-foreground scrollbar-hidden inline-flex h-10 max-w-full items-center justify-center-safe overflow-x-auto rounded-md p-1 pointer-coarse:h-auto', className), ...props }));
}
function TabsTrigger({ ref, className, ...props }) {
    return (_jsx(TabsPrimitive.Trigger, { ref: ref, className: cn('ring-offset-background focus-visible:ring-ring data-[state=active]:bg-background data-[state=active]:text-foreground inline-flex shrink-0 items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm pointer-coarse:min-h-10', className), ...props }));
}
function TabsContent({ ref, className, ...props }) {
    return (_jsx(TabsPrimitive.Content, { ref: ref, className: cn('ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none', className), ...props }));
}
export { Tabs, TabsContent, TabsList, TabsTrigger };
